import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react"

const COMPANY_AUTOCOMPLETE_ACTIONS = {
    SET_INPUT_VALUE: "SET_INPUT_VALUE",
    SET_SELECTED_VALUE: "SET_SELECTED_VALUE",
    SEARCH_START: "SEARCH_START",
    SEARCH_SUCCESS: "SEARCH_SUCCESS",
    SEARCH_ERROR: "SEARCH_ERROR",
    SEARCH_CLEAR: "SEARCH_CLEAR",
    RESET_STATE: "RESET_STATE",
}

const INITIAL_STATE = {
    inputValue: "",
    selectedValue: null,
    results: [],
    loading: false,
    error: null,
}

const reducerHandlers = {
    [COMPANY_AUTOCOMPLETE_ACTIONS.SET_INPUT_VALUE]: (state, payload) => ({
        ...state,
        inputValue: payload ?? "",
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SET_SELECTED_VALUE]: (state, payload) => ({
        ...state,
        selectedValue: payload,
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_START]: (state) => ({
        ...state,
        loading: true,
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_SUCCESS]: (state, payload) => ({
        ...state,
        results: Array.isArray(payload) ? payload : [],
        loading: false,
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_ERROR]: (state, payload) => ({
        ...state,
        results: [],
        loading: false,
        error: payload || "Erro ao pesquisar empresas.",
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_CLEAR]: (state) => ({
        ...state,
        results: [],
        loading: false,
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.RESET_STATE]: () => ({
        ...INITIAL_STATE,
    }),
}


const companyAutocompleteReducer = (state, action) => {
    const handler = reducerHandlers[action.type]

    return handler
        ? handler(state, action.payload)
        : state
}

const normalizeText = (value) =>
    String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, " ")

const normalizeDocument = (value) =>
    String(value ?? "").replace(/\D/g, "")

const getCompanyDocument = (company) =>
    company?.formattedCPFCNPJ ??
    company?.CPFCNPJ ??
    ""

const getCompanyKey = (company) =>
    String(
        company?.CPFCNPJ ??
        company?.formattedCPFCNPJ ??
        company?.RazaoSocial ??
        ""
    )

export const getCompanyOptionLabel = (company) => {
    if (!company) return ""

    if (typeof company === "string") {
        return company
    }

    const razaoSocial = company.RazaoSocial ?? ""
    const cpfCnpj = getCompanyDocument(company)

    if (razaoSocial && cpfCnpj) {
        return `${razaoSocial} | ${cpfCnpj}`
    }

    return razaoSocial || cpfCnpj
}

export const useCompanyAutocomplete = ({
    companies = [],
    onCompanySelect,
    debounceDelay = 300,
    minSearchLength = 2,
    maxResults = 10,
} = {}) => {
    const [open, setOpen] = useState(false)

    const [state, dispatch] = useReducer(
        companyAutocompleteReducer,
        INITIAL_STATE
    )

    const debounceRef = useRef(null)
    const lastInputValueRef = useRef("")

    const actions = useMemo(
        () => ({
            setInputValue: (value) =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SET_INPUT_VALUE,
                    payload: value,
                }),

            setSelectedValue: (value) =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SET_SELECTED_VALUE,
                    payload: value,
                }),

            startSearch: () =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_START,
                }),

            searchSuccess: (results) =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_SUCCESS,
                    payload: results,
                }),

            searchError: (message) =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_ERROR,
                    payload: message,
                }),

            clearSearch: () =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_CLEAR,
                }),

            resetState: () =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.RESET_STATE,
                }),
        }),
        []
    )


    const handleOpen = useCallback(() => {
        setOpen(true)

        if (!state.inputValue.trim()) {
            actions.searchSuccess(
                Array.isArray(companies)
                    ? companies.slice(0, maxResults)
                    : []
            )
        }
    }, [
        actions,
        companies,
        maxResults,
        state.inputValue,
    ])

    const handleClose = useCallback(() => {
        setOpen(false)
        if (!state.selectedValue) {
            actions.clearSearch()
        }
    }, [actions, state.selectedValue])

    const searchCompanies = useCallback(
        (value) => {
            if (!Array.isArray(companies)) {
                return []
            }

            const normalizedText = normalizeText(value)
            const normalizedDocument = normalizeDocument(value)
            const foundKeys = new Set()

            return companies
                .filter((company) => {
                    const razaoSocial = normalizeText(
                        company?.RazaoSocial
                    )

                    const cpfCnpj = normalizeDocument(
                        getCompanyDocument(company)
                    )

                    const matchesRazaoSocial =
                        normalizedText.length > 0 &&
                        razaoSocial.includes(normalizedText)

                    const matchesCpfCnpj =
                        normalizedDocument.length > 0 &&
                        cpfCnpj.includes(normalizedDocument)

                    return matchesRazaoSocial || matchesCpfCnpj
                })
                .filter((company) => {
                    const key = getCompanyKey(company)

                    if (!key || foundKeys.has(key)) {
                        return false
                    }

                    foundKeys.add(key)
                    return true
                })
                .slice(0, maxResults)
        },
        [companies, maxResults]
    )

    const handleInputChange = useCallback(
        (_event, newInputValue, reason) => {
            if (reason === "reset") {
                return
            }

            const input = newInputValue ?? ""

            lastInputValueRef.current = input
            actions.setInputValue(input)

            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }

            if (reason === "clear") {
                lastInputValueRef.current = ""
                actions.setInputValue("")
                actions.setSelectedValue(null)
                actions.clearSearch()
                return
            }

            if (input.trim().length < minSearchLength) {
                actions.clearSearch()
                return
            }

            debounceRef.current = setTimeout(() => {
                actions.startSearch()

                try {
                    const results = searchCompanies(input)
                    actions.searchSuccess(results)
                } catch (error) {
                    console.error(
                        "Erro ao pesquisar empresa:",
                        error
                    )

                    actions.searchError(
                        error?.message ||
                        "Erro ao pesquisar empresas."
                    )
                }
            }, debounceDelay)
        },
        [
            actions,
            debounceDelay,
            minSearchLength,
            onCompanySelect,
            searchCompanies,
        ]
    )

    const handleSelectionChange = useCallback(
        (_event, newValue, reason) => {
            actions.setSelectedValue(newValue)

            if (!newValue || reason === "clear") {
                lastInputValueRef.current = ""
                actions.setSelectedValue(null)
                actions.setInputValue("")
                actions.clearSearch()
                return
            }

            const label = getCompanyOptionLabel(newValue)

            lastInputValueRef.current = label
            actions.setInputValue(label)
            actions.clearSearch()

            onCompanySelect?.(newValue)
        },
        [actions, onCompanySelect]
    )

    const isOptionEqualToValue = useCallback(
        (option, value) =>
            getCompanyKey(option) === getCompanyKey(value),
        []
    )

    const cleanup = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }

        actions.resetState()
    }, [actions])

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
        }
    }, [])

    useEffect(() => {
        /*
         * O input foi aberto enquanto a lista ainda estava carregando.
         * Assim que companies for preenchido, atualizamos as opções
         * sem exigir que o usuário feche e abra novamente.
         */
        if (!open || state.inputValue.trim()) {
            return
        }

        if (!Array.isArray(companies)) {
            return
        }

        actions.searchSuccess(
            companies.slice(0, maxResults)
        )
    }, [
        actions,
        companies,
        maxResults,
        open,
        state.inputValue,
    ])

    const computed = useMemo(
        () => ({
            hasResults: state.results.length > 0,
            hasError: Boolean(state.error),
            isEmpty:
                !state.inputValue &&
                !state.selectedValue,
            isSearching: state.loading,
        }),
        [state]
    )

    return {
        state,
        open,
        actions,
        handlers: {
            onOpen: handleOpen,
            onClose: handleClose,
            onInputChange: handleInputChange,
            onSelectionChange: handleSelectionChange,
            getOptionLabel: getCompanyOptionLabel,
            isOptionEqualToValue,
        },
        computed,
        cleanup,
    }
}

export default useCompanyAutocomplete